import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import {
  searchLocations,
  reverseGeocode,
  GeocodingResult,
} from "../../../utils/geocoding";

interface Props {
  open: boolean;
  dateTime: Date;
  locationName: string;
  /** Current observer coordinates — the picker starts from these. */
  lat: number;
  lon: number;
  /** Tab to open on: 0 Date | 1 Time | 2 Location. */
  initialTab?: number;
  onClose: () => void;
  onSubmit: (
    dateTime: Date,
    lat: number,
    lon: number,
    locationName: string,
  ) => void;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);
const YEARS = Array.from({ length: 201 }, (_, i) => 1900 + i);

const daysInMonth = (year: number, month: number): number =>
  new Date(year, month + 1, 0).getDate();

/** Snap a minute to the nearest 5-minute slot used by the picker. */
const snapMinute = (m: number): number => (Math.round(m / 5) * 5) % 60;

const pad2 = (n: number): string => String(n).padStart(2, "0");

const LocationDateTimeDialog: React.FC<Props> = ({
  open,
  dateTime,
  locationName,
  lat,
  lon,
  initialTab = 0,
  onClose,
  onSubmit,
}) => {
  const [tab, setTab] = useState(initialTab);
  const [year, setYear] = useState(dateTime.getFullYear());
  const [month, setMonth] = useState(dateTime.getMonth());
  const [day, setDay] = useState(dateTime.getDate());
  const [hour, setHour] = useState(dateTime.getHours());
  const [minute, setMinute] = useState(snapMinute(dateTime.getMinutes()));

  const [locLat, setLocLat] = useState(lat);
  const [locLon, setLocLon] = useState(lon);
  const [locName, setLocName] = useState(locationName);
  const [locationInput, setLocationInput] = useState(locationName);
  const [options, setOptions] = useState<GeocodingResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);

  // Snapshot the current date/time/location exactly once, when the dialog
  // opens, so the picker does not follow the astrolabe's live refresh while
  // the user is editing.
  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setYear(dateTime.getFullYear());
    setMonth(dateTime.getMonth());
    setDay(dateTime.getDate());
    setHour(dateTime.getHours());
    setMinute(snapMinute(dateTime.getMinutes()));
    setLocLat(lat);
    setLocLon(lon);
    setLocName(locationName);
    setLocationInput(locationName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const maxDay = daysInMonth(year, month);
  const safeDay = Math.min(day, maxDay);

  // Geocode the typed location name; enforce a minimum length to avoid
  // hammering the (rate-limited) Nominatim API.
  const handleLocationSearch = useCallback(async (_: any, value: string) => {
    setLocationInput(value);
    if (value.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const results = await searchLocations(value);
      setOptions(results);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocLat(latitude);
        setLocLon(longitude);
        setLocName(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        setLocationInput("");
        setLocating(false);
        const result = await reverseGeocode(latitude, longitude);
        if (result) setLocName(result.displayName);
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 10000 },
    );
  }, []);

  const handleSubmit = () => {
    const dt = new Date(year, month, safeDay, hour, minute);
    const fallbackName =
      locName || `${locLat.toFixed(4)}, ${locLon.toFixed(4)}`;
    onSubmit(dt, locLat, locLon, fallbackName);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Date, Time &amp; Location</DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
      >
        <Tab label="Date" />
        <Tab label="Time" />
        <Tab label="Location" />
      </Tabs>
      <DialogContent sx={{ pt: 2 }}>
        {tab === 0 && (
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              select
              label="Month"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              sx={{ flex: 3 }}
            >
              {MONTHS.map((name, index) => (
                <MenuItem key={name} value={index}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Day"
              value={safeDay}
              onChange={(e) => setDay(Number(e.target.value))}
              sx={{ flex: 1.5 }}
            >
              {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ flex: 2 }}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        {tab === 1 && (
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              select
              label="Hour"
              value={hour}
              onChange={(e) => setHour(Number(e.target.value))}
              sx={{ flex: 1 }}
            >
              {HOURS.map((h) => (
                <MenuItem key={h} value={h}>
                  {pad2(h)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Minute"
              value={minute}
              onChange={(e) => setMinute(Number(e.target.value))}
              sx={{ flex: 1 }}
            >
              {MINUTES.map((m) => (
                <MenuItem key={m} value={m}>
                  {pad2(m)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        {tab === 2 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              freeSolo
              options={options}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt.displayName
              }
              loading={loading}
              inputValue={locationInput}
              onInputChange={handleLocationSearch}
              onChange={(_, value) => {
                if (value && typeof value !== "string") {
                  setLocLat(value.lat);
                  setLocLon(value.lon);
                  setLocName(value.displayName);
                  setLocationInput(value.displayName);
                }
              }}
              filterOptions={(x) => x}
              renderOption={(props, option) => (
                <li {...props} key={option.lat + "," + option.lon}>
                  {option.displayName}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  placeholder="Search for a city…"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={
                  locating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <MyLocationIcon />
                  )
                }
                onClick={useMyLocation}
                disabled={locating}
              >
                Use my location
              </Button>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {locName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {`${locLat.toFixed(4)}, ${locLon.toFixed(4)}`}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Set
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationDateTimeDialog;
